// ----------------------------------------------------------------------
// <copyright file="Izenda.HighmapVizEngine.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

// It must export proj4 into global as it is using by highmap to convert
// the lon/lat point into actuall map position in order to properly draw bubble on the map
window.proj4 = require('proj4');

import VizEngine from '../Izenda.VizEngine';
import Highmaps from 'highmaps';
import { exportClass } from 'share/Izenda.ServiceProvider';
import {
  get,
  getOr,
  head,
  curry,
  inRange,
  filter,
  has,
  size,
  last,
  each,
  isEmpty,
  find,
  isNil,
  omitBy,
  set,
  map,
  groupBy,
  escape,
  flow,
  toNumber,
  merge,
  flatMap
} from 'lodash/fp';

/**
 * An implementation of viz engine using Highmap.
 * This viz engine is only used for rendering the Map
 */
@exportClass('HighmapVizEngine')
export default class HighmapVizEngine extends VizEngine {
  static VisualizationLibrary = Highmaps;

  /**
   * Draw visualization into container and return instance with releasing function
   * @param {DOMElement} chartContainer DOM element of virtualization
   * @param {string} chartType Chart type
   * @param {Object} options Chart options
   * @param {Function} onCompleted A callback function to be invoked after finished rendering
   */
  draw(
    chartContainer,
    chartType,
    { applyPointOptionsColor, bubbleSeries, activePointOption, seriesConfig, ...options },
    onCompleted
  ) {
    // Log options for troubleshooting in DEV mode
    __DEV__ && this.logHighchartOptions(options);

    const callback = map => {
      drawBubbleAfterRender(map, bubbleSeries, activePointOption, seriesConfig, applyPointOptionsColor);

      this.afterRender(map);

      onCompleted(map);
    };

    // Draw map
    const mapInstance = new Highmaps.Map(chartContainer, options, callback);

    return {
      mapInstance,
      destroy() {
        mapInstance && mapInstance.hasRendered && mapInstance.destroy();
      }
    };
  }

  /**
   * Call after render
   * @param {Object} map Highmap map instance
   */
  afterRender(map) {}
}

function drawBubbleAfterRender(map, bubbleSeries, activePointOption, seriesConfig, applyPointOptionsColor) {
  // 1. find map range
  const mapRange = findMapRange(map);
  const checkLat = curry(inRange(mapRange.min[0], mapRange.max[0]));
  const checkLon = curry(inRange(mapRange.min[1], mapRange.max[1]));

  // 2. remove points which do not belong to showing-geo-background
  let latlonPoint;
  const addingSeries = filter(serie => {
    serie.data = filter(point => {
      if (has('lat', point) && has('lon', point)) {
        // draw point based on lat/lon
        latlonPoint = map.fromLatLonToPoint(point);
        return checkLat(latlonPoint.x) && checkLon(latlonPoint.y);
      } else {
        // draw point based on geo-json
        return true;
      }
    }, serie.data);

    return !!size(serie.data);
  }, bubbleSeries);

  // 3. apply cell-color of point-options
  activePointOption && applyPointOptionsColor(addingSeries, activePointOption, seriesConfig);

  // 4. add & redraw bubble-series
  const numSeries = size(addingSeries);
  let index = 0;

  while (index < numSeries - 1) {
    map.addSeries(addingSeries[index], false);
    index++;
  }

  map.addSeries(last(addingSeries), true);
}

function findMapRange(map) {
  const mapRange = { min: [], max: [] };

  each(axis => {
    mapRange.min[axis.isXAxis ? 0 : 1] = axis.min;
    mapRange.max[axis.isXAxis ? 0 : 1] = axis.max;
  }, map.axes);

  return mapRange;
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/highcharts/Izenda.HighmapVizEngine.js